import { Button as ChakraButton } from "@chakra-ui/react";
import { VideoKarte } from "./VideoKarte";
import { FeatureHintVideo } from "./useFeatureHints";

/** Ein Knopf unter der Neuigkeit (KAN-276, A7) — `href` ist schon aufgeloest. */
export interface FeatureHintAktion {
  label: string;
  kind: "intern" | "extern";
  href: string;
}

export interface FeatureHintInhalt {
  title: string;
  problemText: string;
  solutionText: string;
  /** Hoechstens zwei; eine Neuigkeit ohne Knopf ist erlaubt (KAN-276, A7). */
  actions?: FeatureHintAktion[];
  /** Bild ueber der Neuigkeit, immer 16:9 beschnitten (KAN-276, A10). */
  imageUrl?: string | null;
  /** Hoechstens eines je Neuigkeit (KAN-276, A12). */
  video?: FeatureHintVideo | null;
  videos?: FeatureHintVideo[];
  /** Bestand: aeltere Eintraege tragen noch den einzelnen Knopf. */
  ctaLabel?: string;
  ctaPath?: string;
}

interface FeatureHintAnsichtProps {
  inhalt: FeatureHintInhalt;
  /** Optional ueber dem Titel, etwa das Datum in der Vollansicht. */
  ueberschrift?: string;
  /** Ein Knopf, der in die App fuehrt — der Aufrufer entscheidet, ob er dabei zumacht. */
  onInternal: (href: string) => void;
  /**
   * Was der Kunde angefasst hat (KAN-296). Die Stelle ist die Reihenfolge im Eintrag, nicht
   * die Beschriftung: Die aendert sich, die Stelle nicht.
   */
  onGriff?: (art: "knopf" | "video", stelle: number) => void;
}

/**
 * Die Darstellung eines Hinweises (KAN-252, A10; erweitert um KAN-276).
 *
 * Zwei Stellen zeigen denselben Hinweis: der Dialog nach dem Anmelden und die Vollansicht im
 * Neuigkeiten-Bereich. Beide benutzen dieses Bauteil, damit ein Hinweis nicht an zwei Orten
 * verschieden aussieht. Wo es sitzt und wie man es schliesst, entscheidet der Aufrufer.
 *
 * Knoepfe nach innen gehen ueber `onInternal`, damit der Aufrufer vorher zumachen kann.
 * Knoepfe nach aussen sind echte Verweise mit `rel="noopener noreferrer"` in einem neuen
 * Reiter — der Kunde verliert nie seine Arbeit in kandidat.io (Verbotsliste 7).
 */
export const FeatureHintAnsicht = ({ inhalt, ueberschrift, onInternal, onGriff }: FeatureHintAnsichtProps) => {
  // Bestandseintraege ohne Knopf-Liste tragen noch das alte Einzelfeld. Beides zu lesen
  // kostet drei Zeilen und erspart eine Datenwanderung.
  const aktionen: FeatureHintAktion[] =
    inhalt.actions && inhalt.actions.length > 0
      ? inhalt.actions
      : inhalt.ctaLabel && inhalt.ctaPath
        ? [{ label: inhalt.ctaLabel, kind: "intern", href: inhalt.ctaPath }]
        : [];

  return (
    <div className="flex flex-col gap-3">
      {inhalt.imageUrl && (
        // Jedes Bild landet im selben Format: feste 16:9-Flaeche, mittig beschnitten. So
        // faellt kein Eintrag aus der Reihe, egal was jemand hochgeladen hat (A10).
        <img
          src={inhalt.imageUrl}
          alt=""
          className="aspect-video w-full rounded-lg bg-gray-100 object-cover object-center"
        />
      )}
      {ueberschrift && <div className="text-xs text-gray-500">{ueberschrift}</div>}
      <h2 className="text-xl font-bold leading-snug text-gray-900">{inhalt.title}</h2>
      {inhalt.problemText && <p className="text-sm leading-relaxed text-gray-500">{inhalt.problemText}</p>}
      {inhalt.solutionText && <p className="text-sm leading-relaxed text-gray-700">{inhalt.solutionText}</p>}
      {/* Unter den Absaetzen, ueber den Knoepfen: Das Video belegt nicht die Bildflaeche,
          damit Bild und Video nebeneinander passen (A12). */}
      {/* Bestand kann noch ein Einzelvideo tragen; neu kommt die Liste (KAN-291). */}
      {(inhalt.videos?.length ? inhalt.videos : inhalt.video ? [inhalt.video] : []).map((video, index) => (
        <VideoKarte key={index} video={video} onAbspielen={() => onGriff?.("video", index)} />
      ))}
      {aktionen.length > 0 && (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {aktionen.map((aktion, stelle) =>
            aktion.kind === "extern" ? (
              <a
                key={`${aktion.kind}-${aktion.href}`}
                href={aktion.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onGriff?.("knopf", stelle)}
                className="inline-flex min-h-9 items-center rounded-md border border-gray-300 px-3 text-sm font-medium text-gray-700"
              >
                {aktion.label}
              </a>
            ) : (
              <ChakraButton
                key={`${aktion.kind}-${aktion.href}`}
                size="sm"
                colorScheme="primary"
                variant="solid"
                className="text-white"
                onClick={() => {
                  onGriff?.("knopf", stelle);
                  onInternal(aktion.href);
                }}
              >
                {aktion.label}
              </ChakraButton>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default FeatureHintAnsicht;
